import GenealogyDiagram, { GenealogyModal } from "@common/components/Diagram/Genealogy/component";
import { mapItem } from "@common/components/Diagram/Genealogy/lib";
import Button from "@common/controls/Button/component";
import FullHeightContainer from "@common/controls/Container/FullHeight";
import FormBlock from "@common/controls/Form/Block/component";
import GridView, { type GridViewProps } from "@common/controls/GridView/component";
import Hint from "@common/controls/Hint/component";
import OnOff from "@common/controls/Inputs/OnOff/component";
import { IconType } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import { sort } from "@common/lib/array";
import { usePromise } from "@common/lib/hooks";
import Network from "@common/modules/network/types";
import { getUserDisplayName } from "@common/modules/user/lib";
import classNames from "classnames";
import * as React from "react";

import "./style.less";

type Props = {
  cycleEndDate: string;
  getItems: (cycleEndDate: string) => Promise<Network.Genealogy[]>;
  getDetail: (item: Network.Genealogy) => Promise<Network.Api.GenealogyDetail>;
  title?: any;
};
const AffiliateGenealogy = (props: Props) => {
  const i18n = useI18n();

  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [mode, setMode] = React.useState<"list" | "diagram">("diagram");
  const [item, setItem] = React.useState<Network.Genealogy | null>(null);

  const [items, isLoadingItems] = usePromise({
    fn: props.getItems,
    args: [props.cycleEndDate],
    shouldLoad: true
  });

  const toggleFullscreen = React.useCallback(() => {
    setIsFullscreen(!isFullscreen);
  }, [isFullscreen]);

  const gridProps = React.useMemo(
    (): GridViewProps<Network.Genealogy, Network.View.Genealogy> => ({
      idKey: "id",
      columns: [
        {
          key: "firstName",
          name: i18n.name,
          dataType: "string",
          data: (x) => getUserDisplayName(x),
          qa: "Name"
        },
        {
          key: "email",
          name: i18n.email,
          dataType: "string",
          data: (x) =>
            x.isSensitive ? <Hint tooltip={i18n.infoAffiliateNetworkHiddenData} hint={i18n.emailHidden} /> : x.email,
          qa: "Email"
        },
        {
          key: "telephone",
          name: i18n.phone,
          dataType: "string",
          data: (x) =>
            x.isSensitive ? (
              <Hint tooltip={i18n.infoAffiliateNetworkHiddenData} hint={i18n.phoneHidden} />
            ) : (
              x.telephone
            ),
          qa: "Phone"
        },
        {
          key: "placedOnDate",
          name: i18n.joinDate,
          dataType: "date",
          qa: "JoinDate"
        }
      ],
      onRowClick: setItem,
      data: sort(
        (items ?? []).filter((x) => x.level > 0),
        { key: "firstName" }
      ),
      isLoading: false,
      toolbar: {
        showAdd: false,
        showSearch: false
      }
    }),
    [items, i18n]
  );

  return (
    <div className="affiliate_analytics downline">
      <div className="toolbar">
        {props.title}

        <OnOff
          onText={i18n.list}
          offText={i18n.downline}
          onChange={(x) => setMode(x ? "list" : "diagram")}
          value={mode === "list"}
        />
      </div>

      {mode === "diagram" ? (
        <FormBlock
          className={classNames("network_block", {
            fullscreen: isFullscreen
          })}
          isLoading={isLoadingItems}
        >
          <Button
            className="fullscreen"
            icon={isFullscreen ? IconType.ExitFullscreen : IconType.Fullscreen}
            type="white"
            onClick={toggleFullscreen}
          />
          <FullHeightContainer className="diagram_container">
            <GenealogyDiagram cycleEndDate={props.cycleEndDate} data={items ?? []} getDetail={props.getDetail} />
          </FullHeightContainer>
        </FormBlock>
      ) : (
        <>
          <GridView {...gridProps} isLoading={isLoadingItems} />

          {item && (
            <GenealogyModal
              hideSensitiveDataTooltip={i18n.infoAffiliateNetworkHiddenData}
              item={mapItem(item)}
              getDetail={props.getDetail}
              exit={() => setItem(null)}
              parameters={{ CycleEndDate: props.cycleEndDate }}
            />
          )}
        </>
      )}
    </div>
  );
};
export default AffiliateGenealogy;
